import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";

function DocumentTypes() {
    const [documentTypes, setDocumentTypes] = useState([]);
    const [newDocumentType, setNewDocumentType] = useState("");
    const [editDocumentType, setEditDocumentType] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        per_page: 15
    });
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [documentTypeToDelete, setDocumentTypeToDelete] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem("access_token");

    const fetchDocumentTypes = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/documents/types?page=${page}&per_page=15`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                // Handle paginated response
                if (data.data) {
                    setDocumentTypes(data.data);
                    setPagination({
                        current_page: data.current_page,
                        total: data.total,
                        per_page: data.per_page,
                        last_page: data.last_page
                    });
                } else {
                    // Fallback for non-paginated response
                    setDocumentTypes(data);
                }
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch the document types");
            }
        } catch (error) {
            console.error("There was an error fetching the document types!", error);
            setNotification({ type: "danger", message: "Failed to fetch document types." });
        } finally {
            setLoading(false);
        }
    }, [token, navigate, location]);

    useEffect(() => {
        fetchDocumentTypes();
    }, [fetchDocumentTypes]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!newDocumentType.trim()) {
            setErrors({ name: ["Document type name is required"] });
            return;
        }

        try {
            const payload = {
                name: newDocumentType.trim(),
                status: 'Active'
            };

            const response = editDocumentType
                ? await fetch(`${API_URL}/documents/types/${editDocumentType.id}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                })
                : await fetch(`${API_URL}/documents/types`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

            if (!response.ok) {
                const errorData = await response.json();
                setErrors(errorData || {});
                return;
            }

            const message = editDocumentType
                ? "Document type updated successfully."
                : "Document type added successfully.";

            setNotification({ type: "success", message });
            setNewDocumentType("");
            setEditDocumentType(null);
            fetchDocumentTypes();
        } catch (error) {
            console.error("There was an error submitting the form!", error);
            setNotification({ type: "danger", message: "Failed to submit the document type." });
        }
    };

    const handleEdit = (documentType) => {
        setNewDocumentType(documentType.name);
        setEditDocumentType(documentType);
    };

    const handleDeleteClick = (documentType) => {
        setDocumentTypeToDelete(documentType);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!documentTypeToDelete) return;

        try {
            const response = await fetch(`${API_URL}/documents/types/${documentTypeToDelete.id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                setNotification({ 
                    type: "danger", 
                    message: errorData.message || "Failed to delete document type." 
                });
                return;
            }

            fetchDocumentTypes();
            setNotification({ type: "success", message: "Document type deleted successfully." });
        } catch (error) {
            console.error("There was an error deleting the document type!", error);
            setNotification({ type: "danger", message: "Failed to delete the document type." });
        } finally {
            setConfirmDialogOpen(false);
            setDocumentTypeToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
        setDocumentTypeToDelete(null);
    };

    const handleCancelEdit = () => {
        setNewDocumentType("");
        setEditDocumentType(null);
        setErrors({});
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            fetchDocumentTypes(newPage);
        }
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Document Types</p>
                    </div>
                </div>
                <div className="level-right">
                    <div className="level-item">
                        <button 
                            className="button is-info"
                            onClick={() => fetchDocumentTypes()}
                            disabled={loading}
                        >
                            <span className="icon">
                                <i className="fas fa-sync"></i>
                            </span>
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </nav>

            <div className="columns">
                <div className="column is-half dash-section vertical-separator">
                    <h1 className="section-title">Document Types</h1>
                    
                    {loading && (
                        <div className="has-text-centered">
                            <i className="fas fa-spinner fa-spin"></i> Loading...
                        </div>
                    )}

                    {!loading && documentTypes.length === 0 && (
                        <div className="notification is-warning">
                            No document types found. Create your first document type!
                        </div>
                    )}

                    {!loading && documentTypes.length > 0 && (
                        <>
                            <table className="table is-striped is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documentTypes.map((documentType, index) => (
                                        <tr key={documentType.id}>
                                            <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                                            <td>{documentType.name}</td>
                                            <td>
                                                <span className={`tag ${documentType.status === 'Active' ? 'is-success' : 'is-danger'}`}>
                                                    {documentType.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="buttons are-small">
                                                    <button
                                                        className="button is-info is-light"
                                                        onClick={() => handleEdit(documentType)}
                                                        title="Edit"
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-edit"></i>
                                                        </span>
                                                    </button>
                                                    <button
                                                        className="button is-danger is-light"
                                                        onClick={() => handleDeleteClick(documentType)}
                                                        title="Delete"
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-trash-alt"></i>
                                                        </span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {pagination.last_page > 1 && (
                                <nav className="pagination is-centered" role="navigation">
                                    <button
                                        className="pagination-previous"
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        className="pagination-next"
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    >
                                        Next
                                    </button>
                                    <ul className="pagination-list">
                                        {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                                            <li key={page}>
                                                <button
                                                    className={`pagination-link ${pagination.current_page === page ? 'is-current' : ''}`}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            )}
                        </>
                    )}
                </div>

                <div className="column is-half dash-section">
                    <h1 className="section-title">
                        {editDocumentType ? "Edit Document Type" : "Add New Document Type"}
                    </h1>

                    {notification.message && (
                        <Notification
                            type={notification.type}
                            message={notification.message}
                            onClose={closeNotification}
                        />
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="field">
                            <label className="label">Document Type Name</label>
                            <div className="control has-icons-left">
                                <input
                                    className={`input ${errors.name ? "is-danger" : ""}`}
                                    type="text"
                                    placeholder="e.g. E-Books, Reports, Training Materials"
                                    value={newDocumentType}
                                    onChange={(e) => setNewDocumentType(e.target.value)}
                                    required
                                />
                                <span className="icon is-small is-left">
                                    <i className="fas fa-folder"></i>
                                </span>
                            </div>
                            {errors.name && errors.name.map((error, index) => (
                                <p key={index} className="help is-danger">{error}</p>
                            ))}
                        </div>

                        {errors.description && errors.description.map((error, index) => (
                            <p key={index} className="help is-danger">{error}</p>
                        ))}

                        <div className="field is-grouped">
                            <div className="control">
                                <button 
                                    type="submit" 
                                    className="button is-primary"
                                    disabled={!newDocumentType.trim()}
                                >
                                    {editDocumentType ? "Update" : "Add New"} Document Type
                                </button>
                            </div>
                            {editDocumentType && (
                                <div className="control">
                                    <button 
                                        type="button" 
                                        className="button is-light"
                                        onClick={handleCancelEdit}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>

                    <div className="content mt-5">
                        <h4>Tips:</h4>
                        <ul>
                            <li>Use clear, descriptive names for document types</li>
                            <li>Examples: "Mission E-Books", "Training Materials"</li>
                            <li>You cannot delete document types that are in use by documents</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* ConfirmDialog Component */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                theme="is-danger"
                title="Delete Document Type"
                message={`Are you sure you want to delete the document type "${documentTypeToDelete?.name}"? This action cannot be undone.`}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </main>
    );
}

export default DocumentTypes;